import { useState, useEffect } from 'react';
import { doc, deleteDoc, setDoc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { formatDistanceToNow } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@/components/AuthProvider';
import CreatePostForm from '@/components/content/CreatePostForm';
import { toast } from 'sonner';
import RichText from '@/components/ui/RichText';
import PostPreviewCard from '@/components/content/PostPreviewCard';
import { Button } from '@/components/ui/Button';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PRESET_AMOUNTS, PRESET_CURRENCY, formatPresetAmounts } from '@/lib/donations/amounts';
import { useImpression } from '@/hooks/useImpression';
import { trackDonateAskViewed } from '@/lib/utils/analytics';

interface Post {
  id: string;
  userId: string;
  title: string;
  content: string;
  type: 'text' | 'video' | 'image';
  videoUrl?: string;
  images?: string[];
  publishingOptions?: {
    access: 'paid_members' | 'public' | 'members';
    price: string;
    earlyAccess: boolean;
    releaseDate: Date | null;
  };
  createdAt: any;
  updatedAt?: any;
  stats?: {
    likes: number;
    comments: number;
    impressions: number;
  };
  isLocked?: boolean;
  isPinned?: boolean;
  likedBy?: string[];
  likes?: string[];
}

interface PostListProps {
  posts: Post[];
  userName: string;
  onPostUpdated: (post: Post) => void;
  onRefreshPosts?: () => void;
  onCreateClick?: () => void;
  isSubscribed?: boolean;
  creatorId?: string;
}

export default function PostList({ posts: initialPosts, userName, onPostUpdated, onRefreshPosts, onCreateClick, isSubscribed, creatorId }: PostListProps) {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [isPostFormOpen, setIsPostFormOpen] = useState(false);
  const [localPosts, setLocalPosts] = useState<Post[]>(initialPosts);

  useEffect(() => {
    setLocalPosts(initialPosts);
  }, [initialPosts]);

  // How many people were ever SHOWN the ask? Fires once, only when the
  // supporter card — the one public surface that names real amounts — is
  // genuinely half on screen, and never for the creator themselves: not on
  // their own dashboard (`onCreateClick` is the owner view) and not when
  // they are logged in and looking at their own public page, which this
  // same component also renders (MissionContent passes no onCreateClick).
  const isOwnPage = !!user && user.uid === creatorId;
  const askRef = useImpression<HTMLDivElement>(
    () =>
      trackDonateAskViewed({
        amountsShown: true,
        amounts: PRESET_AMOUNTS,
        currency: PRESET_CURRENCY,
        surface: 'supporter_card',
        creatorId: creatorId ?? null,
      }),
    !onCreateClick && !isOwnPage && !authLoading,
  );

  const formatDate = (timestamp: any) => {
    if (timestamp instanceof Timestamp) {
      return formatDistanceToNow(timestamp.toDate(), { addSuffix: true });
    }
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  const handleEdit = (post: Post) => {
    setEditingPost(post);
    setIsPostFormOpen(true);
  };

  const handleLike = async (post: Post) => {
    if (!user) {
      toast.error(t('posts.list.signInToLike'));
      return;
    }

    const likeId = `${post.id}_${user.uid}`;
    const likeRef = doc(db, 'likes', likeId);
    const postRef = doc(db, 'posts', post.id);

    try {
      const likeDoc = await getDoc(likeRef);
      const isLiked = likeDoc.exists();

      const updatedLikes = isLiked
        ? (post.likes || []).filter(id => id !== user.uid)
        : [...(post.likes || []), user.uid];

      const updatedPost: Post = {
        ...post,
        likes: updatedLikes,
      };

      setLocalPosts(currentPosts =>
        currentPosts.map(p => p.id === post.id ? updatedPost : p)
      );

      if (isLiked) {
        await deleteDoc(likeRef);
      } else {
        await setDoc(likeRef, {
          postId: post.id,
          userId: user.uid,
          createdAt: serverTimestamp()
        });
      }

      await updateDoc(postRef, {
        likes: updatedLikes
      });

      if (onPostUpdated) {
        onPostUpdated(updatedPost);
      }

      toast.success(isLiked ? t('posts.list.likeRemoved') : t('posts.list.postLiked'));
    } catch (error) {
      console.error('[PostList] Error:', error);
      toast.error(t('posts.list.likeFailed'));
      // Revert the local state on error
      setLocalPosts(currentPosts =>
        currentPosts.map(p => p.id === post.id ? post : p)
      );
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {/* Left: Posts. order-2 on mobile so the supporter card (order-1) is
          reachable without scrolling past the whole feed -- on a single
          shared-link visit the ask is otherwise the last thing on the page,
          after the hero/bio/every post (TSF-312). md: reverts to the normal
          left/right order. */}
      <div className="order-2 md:order-1 md:col-span-2">
        <h2 className="text-2xl font-bold mb-6">{t('posts.list.recent')}</h2>
        {localPosts.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-600 mb-6">{t('posts.list.emptyMessage')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              {onCreateClick && (
                <Button onClick={onCreateClick} className="min-w-[200px]">
                  {t('posts.list.createFirst')}
                </Button>
              )}
              <Button
                variant="outline"
                className="min-w-[200px]"
                onClick={() => {
                  const url = `/fund/demo`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
              >
                {t('posts.list.viewSample')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {localPosts.map((post) => (
              <PostPreviewCard
                key={post.id}
                post={post}
                user={user}
                onPostUpdated={onPostUpdated}
                creatorName={userName}
                isSubscribed={isSubscribed}
              />
            ))}
          </div>
        )}
      </div>
      {/* Right: supporter block. Sticky beside the posts on desktop; on a phone
          the grid collapses to one column and this simply follows the posts.
          It used to be `hidden md:block`, which meant a supporter who opened a
          shared fund link on their phone had NO donate button anywhere on the
          page -- and a shared link is mostly opened on a phone. */}
      <div className="order-1 md:order-2 md:col-span-1">
        <div className="md:sticky md:top-24">
          {onCreateClick && (
            <div className="bg-white rounded-lg shadow p-6 mb-8">
              <Button
                onClick={onCreateClick}
                className="w-full flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                <span>{t('posts.list.create')}</span>
              </Button>
            </div>
          )}
          <div ref={askRef} className="bg-white rounded-lg shadow p-6 mb-8">
            <h3 className="text-lg font-bold mb-2">{t('posts.list.becomeSupporterTitle')}</h3>
            <p className="text-gray-600 mb-3 text-sm">{t('posts.list.becomeSupporterDesc')}</p>
            {/* The ask, with a number in it. Until this line the whole public
                page said "Support my journey" and named no amount anywhere,
                so nobody could be said to have been shown an ask at all. */}
            <p className="text-gray-900 font-semibold mb-4 text-sm">
              {t('posts.list.supportAmounts', { amounts: formatPresetAmounts() })}
            </p>
            <Button
              className="w-full mb-2"
              onClick={() => {
                const donateSection = document.getElementById('donate');
                if (donateSection) {
                  donateSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                } else {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
                // Notify page to open donation form
                window.dispatchEvent(new CustomEvent('open-donate', { detail: { surface: 'supporter_card' } }));
              }}
            >
              {t('posts.list.support')}
            </Button>
            {/* <Button variant="outline" className="w-full">Gift membership</Button> */}
          </div>
          {/* You can add more right-side widgets here if needed */}
        </div>
      </div>

      <CreatePostForm
        isOpen={isPostFormOpen}
        onClose={() => {
          setIsPostFormOpen(false);
          setEditingPost(null);
        }}
        initialPost={editingPost}
        onPostSaved={(savedPost) => {
          if (savedPost === null) {
            // Post was deleted
            setLocalPosts(currentPosts =>
              currentPosts.filter(post => post.id !== editingPost?.id)
            );
            // Notify parent about the deletion
            onRefreshPosts?.();
          } else {
            // Post was updated
            setLocalPosts(currentPosts =>
              currentPosts.map(post => post.id === savedPost.id ? savedPost : post)
            );
            // Close the dialog
            setIsPostFormOpen(false);
            setEditingPost(null);
            // Notify parent about the update
            if (onPostUpdated) {
              onPostUpdated(savedPost);
            }
          }
        }}
      />
    </div>
  );
}